import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, LinearProgress, Typography } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import AnalyticsWarehousePicker from "../../components/analytics-warehouse-picker";
import ClientAutocomplete from "../../components/clientAutocomplete";
import AnalysisPageTopbar from "../../components/metrics/AnalysisPageTopbar";
import SalesPerformancePageLoading from "../../components/metrics/SalesPerformancePageLoading";
import { PAGE_FONT, PillTabs, useMetricsUi } from "../../components/metrics/metricsUi";
import { isMobile } from "../../globalStyles/mobile";
import { analyticsWarehouseService } from "../../utils/services/analyticsWarehouseService";
import { purchaseOrderAnalysisService } from "../../utils/services/purchaseOrderAnalysisService";
import { selectableWarehouse } from "../../utils/inventoryHealthPageDefaults";
import {
  DEFAULT_LOOKBACK,
  LOOKBACK_OPTIONS,
  baseParams,
  coverageColor,
  fmtDay,
  fmtLead,
  fmtMoney,
  fmtPct,
  fmtUnits,
} from "../../utils/purchaseOrderAnalysisPageDefaults";
import CoverageSection from "./CoverageSection";
import InboundCalendar from "./InboundCalendar";
import PoDrawer from "./PoDrawer";
import PoTable from "./PoTable";
import ReliabilitySection from "./ReliabilitySection";
import SkuLookup, { SkuDrawer } from "./SkuLookup";
import { KpiCard, StatePanel, eyebrowSx, useLatestRequest } from "./parts";

const FIELD_HEIGHT = 40;
const PO_TABLE_ID = "po-analysis-pos";
const SKU_LOOKUP_ID = "po-analysis-sku";

const scrollTo = (id) => {
  const node = document.getElementById(id);
  if (node && typeof node.scrollIntoView === "function") node.scrollIntoView({ behavior: "smooth", block: "start" });
};

const PurchaseOrderAnalysisPage = () => {
  const ui = useMetricsUi();
  const { colors, darkMode } = ui;
  const mobile = isMobile();
  const storedClientId = localStorage.getItem("client_id");
  // Deep link (e.g. from the Command Center): { client?, warehouse_id, warehouse_name?, sku? | po_id? },
  // applied once when the page opens.
  const { state: linkState } = useLocation();
  const deepLink = useRef(linkState && linkState.warehouse_id ? linkState : null);
  const [selectedClientData, setSelectedClientData] = useState(() => deepLink.current?.client || null);
  const clientId = selectedClientData?._id || storedClientId;

  // One warehouse at a time: coverage joins that warehouse's stock with the
  // POs landing there, never a blend of warehouses.
  const [warehouse, setWarehouse] = useState(null);
  const warehouseId = warehouse?._id || null;
  const [months, setMonths] = useState(DEFAULT_LOOKBACK);

  const [coverageStatus, setCoverageStatus] = useState("needs_po");
  const [poGroup, setPoGroup] = useState("open");
  const [bucket, setBucket] = useState(null);
  const [openPoId, setOpenPoId] = useState(null);
  const [drawerSku, setDrawerSku] = useState(null);
  const [lookupSku, setLookupSku] = useState(null);

  useEffect(() => {
    const link = deepLink.current;
    deepLink.current = null;
    setWarehouse(
      link
        ? { _id: link.warehouse_id, name: link.warehouse_name || null, label: link.warehouse_name || link.warehouse_id }
        : null,
    );
    setOpenPoId(link?.po_id || null);
    setDrawerSku(link?.sku || null);
    setLookupSku(null);
    if (!clientId) return undefined;
    let cancelled = false;
    analyticsWarehouseService
      .fetchDefault(clientId)
      .then((preferred) => {
        if (!cancelled && preferred) setWarehouse((current) => current || selectableWarehouse(preferred));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const base = useMemo(
    () => baseParams({ clientId: storedClientId ? null : clientId, warehouseId, months }),
    [storedClientId, clientId, warehouseId, months],
  );

  const overviewState = useLatestRequest(
    clientId && warehouseId
      ? (signal) => purchaseOrderAnalysisService.fetchOverview(base, { signal })
      : null,
    [base],
  );
  // Never render another warehouse's (or lookback's) numbers while the next one loads.
  const overview =
    overviewState.data &&
    overviewState.data.warehouse?.id === warehouseId &&
    overviewState.data.lookback?.months === months
      ? overviewState.data
      : null;

  const onWarehouseChange = (item) => {
    setWarehouse(selectableWarehouse(item));
    setBucket(null);
    setOpenPoId(null);
    setDrawerSku(null);
  };

  const showPos = (group, nextBucket = null) => {
    setPoGroup(group);
    setBucket(nextBucket);
    scrollTo(PO_TABLE_ID);
  };

  const openSkuFromPo = (sku) => {
    setOpenPoId(null);
    setDrawerSku(sku);
  };

  const kpis = overview
    ? (() => {
        const { coverage, pipeline, reliability, horizon } = overview;
        const rel = reliability.summary;
        const uncoveredRevenue = coverage.revenue_30d_by_status.none + coverage.revenue_30d_by_status.unconfirmed;
        return [
          {
            key: "needs_po",
            label: "Needs a PO",
            value: `${fmtUnits(coverage.needs_po)} SKUs`,
            hint: `run out within ${horizon.days}d with nothing reliable on order · ${fmtMoney(uncoveredRevenue)}/30d${
              coverage.long_stockouts?.needs_po ? ` · +${fmtUnits(coverage.long_stockouts.needs_po)} out all month` : ""
            }`,
            color: coverageColor("none", darkMode),
            onClick: () => setCoverageStatus(coverageStatus === "needs_po" ? "all" : "needs_po"),
            active: coverageStatus === "needs_po",
            tip: "Selling SKUs whose days of cover is shorter than the lead-time horizon, with no open PO (or only POs more than 30 days overdue)",
          },
          {
            key: "late",
            label: "PO arrives too late",
            value: `${fmtUnits(coverage.late)} SKUs`,
            hint: coverage.late ? `≈${fmtUnits(coverage.late_units_short)} units short before the PO lands` : "no scheduled PO lands after a stock-out",
            color: coverageColor("late", darkMode),
            onClick: () => setCoverageStatus(coverageStatus === "late" ? "all" : "late"),
            active: coverageStatus === "late",
          },
          {
            key: "inbound",
            label: "Units on order",
            value: fmtUnits(pipeline.open_units),
            hint: `${fmtUnits(pipeline.open_pos)} open POs · ${fmtUnits(pipeline.open_skus)} SKUs · last PO ${fmtDay(pipeline.last_po_date)}`,
            color: ui.accent,
            onClick: () => showPos("open"),
          },
          {
            key: "overdue",
            label: "Overdue POs",
            value: fmtUnits(pipeline.overdue_pos + pipeline.unconfirmed_pos),
            hint: `${fmtUnits(pipeline.unconfirmed_pos)} stale >30d · ${fmtUnits(pipeline.overdue_units + pipeline.unconfirmed_units)} units`,
            color: coverageColor("unconfirmed", darkMode),
            onClick: () => showPos("overdue"),
          },
          {
            key: "on_time",
            label: "On-time delivery",
            value: fmtPct(rel.on_time_pct),
            hint: rel.timed_pos
              ? `${fmtUnits(rel.timed_pos)} POs · late ones ${fmtLead(rel.avg_days_late)} late on average`
              : "no received POs with an expected date",
            color: coverageColor("covered", darkMode),
          },
          {
            key: "lead",
            label: "PO lead time",
            value: fmtLead(rel.median_lead_days),
            hint: rel.median_lead_days != null ? `median · promised ${fmtLead(rel.median_planned_days)} · p90 ${fmtLead(rel.p90_lead_days)}` : "not enough received POs",
            color: colors.textTertiary,
          },
        ];
      })()
    : [];

  return (
    <Box sx={{ minHeight: "100%", backgroundColor: darkMode ? colors.bgPrimary : "#F5F6F8", fontFamily: PAGE_FONT, textAlign: "left" }}>
      {overviewState.loading && <LinearProgress sx={{ position: "sticky", top: 0, zIndex: 10, height: 3 }} />}
      <Box sx={{ maxWidth: 1480, mx: "auto", padding: mobile ? "14px 12px 40px" : "22px 28px 56px" }}>
        <AnalysisPageTopbar
          badge="PO"
          title="Purchase Order Analysis"
          clientName={selectedClientData?.business_name || selectedClientData?.name}
          right={
            !storedClientId ? (
              <ClientAutocomplete width={220} selectedClient={selectedClientData} onClientChange={setSelectedClientData} />
            ) : null
          }
        />

        <Box sx={{ ...ui.card, p: mobile ? "14px" : "16px 18px 18px", mb: 2.5 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: mobile ? "1fr" : "minmax(260px, 340px) auto 1fr",
              gap: 2,
              alignItems: "end",
            }}
          >
            <Box>
              <Typography sx={{ ...eyebrowSx(colors), mb: 0.65 }}>Warehouse (required)</Typography>
              <AnalyticsWarehousePicker
                clientId={clientId}
                mode="single"
                allowAll={false}
                value={warehouse}
                onChange={onWarehouseChange}
                placeholder="Select a warehouse"
                disabled={!clientId}
                width="100%"
                height={FIELD_HEIGHT}
                triggerSx={{ fontFamily: PAGE_FONT, fontSize: 14, fontWeight: 400 }}
              />
            </Box>
            <Box>
              <Typography sx={{ ...eyebrowSx(colors), mb: 0.65 }}>PO history</Typography>
              <PillTabs
                tabs={LOOKBACK_OPTIONS}
                value={months}
                onChange={(value) => {
                  setMonths(value);
                  setBucket(null);
                }}
              />
            </Box>
            {overview ? (
              <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, lineHeight: 1.55 }}>
                <strong>{overview.warehouse?.name}</strong> · open POs of any age + POs raised since{" "}
                {fmtDay(overview.lookback.since)} · stock as of {fmtDay(overview.today)}, 30-day sales{" "}
                {overview.sales_window ? `to ${fmtDay(overview.sales_window.end)}` : "— no order history"}
              </Typography>
            ) : null}
          </Box>
          {overview?.sales_window?.is_stale ? (
            <Box sx={{ display: "flex", gap: 1, mt: 1.5, px: 1.5, py: 1, borderRadius: "10px", backgroundColor: ui.warningBg }}>
              <WarningAmberOutlinedIcon sx={{ fontSize: 16, color: ui.warning, mt: "1px" }} />
              <Typography sx={{ fontSize: 12.5, color: ui.warning, fontWeight: 500 }}>
                Sales data last updated {fmtDay(overview.sales_window.end)} — days of cover may be out of date.
              </Typography>
            </Box>
          ) : null}
        </Box>

        {!clientId && <StatePanel ui={ui} eyebrow="Client required" title="Select a client to analyse purchase orders." />}
        {clientId && !warehouseId && (
          <StatePanel
            ui={ui}
            eyebrow="Warehouse required"
            title="Select a warehouse to load its purchase orders."
            body="Coverage compares one warehouse's stock with the POs landing there."
          />
        )}
        {clientId && warehouseId && overviewState.error && (
          <StatePanel ui={ui} tone="error" eyebrow="Could not load" title={overviewState.error} />
        )}
        {clientId && warehouseId && !overviewState.error && !overview && (
          <SalesPerformancePageLoading
            title={`Loading purchase orders for ${warehouse?.warehouse_code || "warehouse"}…`}
            kpiCount={6}
          />
        )}

        {overview && !overviewState.error && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: mobile ? "repeat(2, minmax(0, 1fr))" : "repeat(6, minmax(0, 1fr))",
                gap: 1.5,
              }}
            >
              {kpis.map(({ key, ...card }) => (
                <KpiCard key={key} ui={ui} {...card} />
              ))}
            </Box>

            <CoverageSection
              ui={ui}
              base={base}
              overview={overview}
              statusFilter={coverageStatus}
              onStatusFilter={setCoverageStatus}
              onOpenSku={setDrawerSku}
              mobile={mobile}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "minmax(0, 1fr) minmax(0, 1fr)", gap: 2.5 }}>
              <InboundCalendar
                ui={ui}
                overview={overview}
                activeBucket={bucket}
                onPickBucket={(key) => showPos("all", bucket === key ? null : key)}
              />
              <ReliabilitySection ui={ui} overview={overview} onOpenPo={setOpenPoId} />
            </Box>

            <PoTable
              id={PO_TABLE_ID}
              ui={ui}
              base={base}
              group={poGroup}
              onGroup={setPoGroup}
              bucket={bucket}
              onClearBucket={() => setBucket(null)}
              calendar={overview.calendar}
              onOpenPo={setOpenPoId}
              mobile={mobile}
            />

            <SkuLookup id={SKU_LOOKUP_ID} ui={ui} base={base} sku={lookupSku} onSku={setLookupSku} onOpenPo={setOpenPoId} />

            <Typography sx={{ fontSize: 11.5, color: colors.textTertiary, lineHeight: 1.6 }}>
              {overview.definitions.stage}. {overview.definitions.no_date}. {overview.definitions.unconfirmed}.
              {overview.data_quality.pos_without_warehouse
                ? ` ${fmtUnits(overview.data_quality.pos_without_warehouse)} POs could not be matched to any warehouse and are not shown.`
                : ""}{" "}
              Data as of {new Date(overview.data_as_of).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} (refreshes every 5 minutes).
            </Typography>
          </Box>
        )}
      </Box>

      <SkuDrawer ui={ui} sku={drawerSku} base={base} onClose={() => setDrawerSku(null)} onOpenPo={setOpenPoId} />
      <PoDrawer ui={ui} poId={openPoId} base={base} onClose={() => setOpenPoId(null)} onOpenSku={openSkuFromPo} />
    </Box>
  );
};

export default PurchaseOrderAnalysisPage;
